// src/pages/sessions/SessionsList.tsx
import { logger } from '../../utils/logger';

import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonList,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonChip,
  IonFab,
  IonFabButton,
  IonRefresher,
  IonRefresherContent,
  IonLabel,
  IonText,
} from '@ionic/react';
import { add, calendar, people, location, videocam, checkmarkCircle, timeOutline, closeCircle, helpCircle } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { sessionService } from '../../services/sessionService';
import { profileService } from '../../services/profileService';
import { supabase } from '../../services/supabase';
import { SessionWithDetails } from '../../types/session';
import { Profile } from '../../types/profile';
import { AvatarPreview } from '../../components/avatar/AvatarPreview';
import { isProfileComplete, getProfileIncompleteMessage } from '../../utils/profileUtils';
import { ProfileIncompletePrompt } from '../../components/ProfileIncompletePrompt';
import { useProfile } from '../../contexts/ProfileContext';
import { fetchDataWithRetry, updateDataWithRetry } from '../../services/retryService';
import { handleError } from '../../services/errorHandler';
import './SessionsList.css';

type SessionFilter = 'upcoming' | 'past' | 'all';

const SessionsList: React.FC = () => {
  const history = useHistory();
  const { profile, isComplete: profileComplete, loading: profileLoading } = useProfile();
  const [sessions, setSessions] = useState<SessionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<SessionFilter>('upcoming');
  const [currentUserId, setCurrentUserId] = useState<string>('');

  useEffect(() => {
    init();
  }, []);

  // Load sessions when profile becomes complete
  useEffect(() => {
    if (profileComplete && !loading) {
      loadSessions();
    }
  }, [profileComplete]);

  const init = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }


    if (profileComplete) {
      await loadSessions();
    }
    setLoading(false);
  };

  // Ã°Å¸â€Â§ FIXED: Removed sessions dependency to prevent infinite loop
  useEffect(() => {
    let isUpdating = false; // Prevent overlapping updates

    const updateSessionStatuses = async () => {
      // Prevent multiple simultaneous updates
      if (isUpdating) return;

      try {
        // Get fresh session data directly instead of relying on state
        const result = await fetchDataWithRetry(
          () => sessionService.getMySessions()
        );

        if (!result.success || !result.data || result.data.length === 0) return;

        const currentSessions = result.data;

        isUpdating = true;

        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

        let needsRefresh = false;

        for (const session of currentSessions) {
          const sessionDateTime = new Date(`${session.session_date}T${session.start_time}`);
          const sessionEndTime = session.end_time ? new Date(`${session.session_date}T${session.end_time}`) : null;

          if (session.status !== 'completed' && session.status !== 'cancelled') {
            if (sessionEndTime && now > sessionEndTime) {
              await updateDataWithRetry(
                () => sessionService.updateSession(session.id, { status: 'completed' })
              );
              needsRefresh = true;
            } else if (!session.end_time && session.session_date < today) {
              const fourHoursAgo = new Date(now.getTime() - (4 * 60 * 60 * 1000));
              if (sessionDateTime < fourHoursAgo) {
                await updateDataWithRetry(
                  () => sessionService.updateSession(session.id, { status: 'completed' })
                );
                needsRefresh = true;
              }
            }
          }

          if (session.status === 'planning' && session.session_date === today && session.start_time <= currentTime) {
            await updateDataWithRetry(
              () => sessionService.updateSession(session.id, { status: 'confirmed' })
            );
            needsRefresh = true;
          }
        }

        if (needsRefresh) {
          await loadSessions();
        }
      } catch (error) {
        const appError = handleError(error, 'update session statuses');
        logger.error(appError.userMessage);
      } finally {
        isUpdating = false;
      }
    };

    // Run once on mount
    updateSessionStatuses();

    // Then run every 5 minutes
    const interval = setInterval(updateSessionStatuses, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []); // Ã°Å¸Å½Â¯ Empty dependency array - only run once on mount

  const loadSessions = async () => {
    setLoading(true);

    try {
      const result = await fetchDataWithRetry(
        () => sessionService.getMySessions()
      );

      if (result.success && result.data) {
        setSessions(result.data);
      } else {
        logger.error('Failed to load sessions:', result.error?.userMessage || 'Unknown error');
        setSessions([]);
      }
    } catch (error) {
      const appError = handleError(error, 'load sessions');
      logger.error(appError.userMessage);
      setSessions([]);
    }

    setLoading(false);
  };

  const handleRefresh = async (event: CustomEvent) => {
    await init();
    event.detail.complete();
  };

  const getFilteredSessions = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    if (filter === 'upcoming') {
      return sessions.filter(s => {
        if (s.status === 'completed' || s.status === 'cancelled') return false;
        return s.session_date >= today;
      });
    } else if (filter === 'past') {
      return sessions.filter(s => {
        return s.status === 'completed' ||
               s.status === 'cancelled' ||
               (s.session_date < today && s.status !== 'confirmed');
      });
    }
    return sessions;
  };

  const filteredSessions = getFilteredSessions();

  const getRSVPColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'declined': return 'danger';
      case 'maybe': return 'warning';
      default: return 'medium';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'planning': return 'primary';
      case 'completed': return 'medium';
      case 'cancelled': return 'danger';
      default: return 'medium';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getUserRSVP = (session: SessionWithDetails) => {
    if (!currentUserId) return 'pending';
    const participant = session.participants?.find((p: any) => p.user_id === currentUserId);
    return participant?.rsvp_status || 'pending';
  };

  // Ã¢Å“â€¦ FIXED: Proper emoji encoding and improved onboarding
  const renderOnboarding = () => {
    return <ProfileIncompletePrompt profile={profile} actionContext="hosting and joining gaming sessions" />;
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Gaming Sessions</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={filter} onIonChange={e => setFilter(e.detail.value as SessionFilter)}>
            <IonSegmentButton value="upcoming">
              <IonLabel>Soon</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="past">
              <IonLabel>Past</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="all">
              <IonLabel>All</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <IonSpinner />
          </div>
        ) : !profileComplete ? (
          renderOnboarding()
        ) : filteredSessions.length === 0 ? (
          <div className="empty-state">
            <IonIcon icon={calendar} style={{ fontSize: '64px', color: 'var(--ion-color-medium)' }} />
            <h2>No sessions yet</h2>
            <p>Create your first gaming session to get started!</p>
            <IonButton onClick={() => history.push('/tabs/sessions/create')}>
              Create Session
            </IonButton>
          </div>
        ) : (
          <IonList>
            {filteredSessions.map(session => {
              const acceptedCount = session.participants?.filter((p: any) => p.rsvp_status === 'accepted').length || 0;
              const totalParticipants = session.participants?.length || 0;

              return (
                <IonCard key={session.id} button onClick={() => history.push(`/tabs/sessions/${session.id}`)}>
                  <IonCardHeader>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <IonCardTitle>{session.title}</IonCardTitle>
                        <IonCardSubtitle>
                          <IonIcon icon={calendar} style={{ marginRight: '4px' }} />
                          {formatDate(session.session_date)} at {formatTime(session.start_time)}
                        </IonCardSubtitle>
                      </div>
                      <IonBadge color={getStatusColor(session.status)}>
                        {session.status}
                      </IonBadge>
                    </div>
                  </IonCardHeader>

                  <IonCardContent>
                    {session.description && (
                      <p style={{ marginBottom: '12px', color: 'var(--ion-color-medium)' }}>
                        {session.description}
                      </p>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                      <IonChip color={session.location_type === 'in-person' ? 'tertiary' : 'secondary'}>
                        <IonIcon icon={session.location_type === 'in-person' ? location : videocam} />
                        <IonLabel>{session.location_type === 'in-person' ? 'In-Person' : 'Online'}</IonLabel>
                      </IonChip>

                      <IonChip>
                        <IonIcon icon={people} />
                        <IonLabel>{acceptedCount}/{totalParticipants} attending</IonLabel>
                      </IonChip>

                      {session.max_players && (
                        <IonChip color="primary" outline>
                          <IonLabel>Max: {session.max_players}</IonLabel>
                        </IonChip>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <AvatarPreview
                        avatarData={session.host?.avatar}
                        size={24}
                        userId={session.host_id}
                        isAdmin={session.host?.is_admin || false}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--ion-color-medium)' }}>
                        Hosted by {session.host?.display_name || session.host?.username}
                      </span>
                    </div>

                    <IonChip color={getRSVPColor(getUserRSVP(session))}>
                      <IonLabel>Your RSVP: {getUserRSVP(session)}</IonLabel>
                    </IonChip>
                  </IonCardContent>
                </IonCard>
              );
            })}
          </IonList>
        )}

        {profileComplete && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => history.push('/tabs/sessions/create')}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SessionsList;